import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import { filterNavPages, type NavPage } from './nav-model'
import type { PaletteApi } from './useNavShell'

function PaletteBody({ pages, open, onClose }: { pages: NavPage[]; open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [sel, setSel] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const listId = useId()
  const results = useMemo(() => filterNavPages(pages, query), [pages, query])
  const current = Math.min(sel, Math.max(results.length - 1, 0))

  useEffect(() => {
    if (!open) return
    const id = window.requestAnimationFrame(() => input.current?.focus())
    return () => window.cancelAnimationFrame(id)
  }, [open])

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [current])

  const go = (index: number) => {
    const page = results[index]
    if (!page) return
    onClose()
    navigate(page.href)
  }

  return (
    <>
      <div className="ns-cmdk-in">
        <Search />
        <input
          ref={input}
          value={query}
          placeholder="Go to page…"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={results[current] ? `${listId}-${results[current].id}` : undefined}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            setQuery(e.target.value)
            setSel(0)
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setSel(Math.min(current + 1, results.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setSel(Math.max(current - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              go(current)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              onClose()
            }
          }}
        />
      </div>
      <ul ref={list} id={listId} role="listbox" aria-label="Pages">
        {results.map((page, i) => (
          <li
            key={page.id}
            id={`${listId}-${page.id}`}
            role="option"
            aria-selected={i === current}
            onMouseMove={() => i !== current && setSel(i)}
            onClick={() => go(i)}
          >
            <page.icon strokeWidth={1.75} />
            {page.label}
            {page.group ? <small>{page.group}</small> : null}
          </li>
        ))}
      </ul>
      {results.length === 0 ? <div className="ns-cmdk-empty">No matching page</div> : null}
    </>
  )
}

/** Ctrl+K "go to page" palette over the permission-filtered nav pages. */
export function CommandPalette({ palette, pages }: { palette: PaletteApi; pages: NavPage[] }) {
  return (
    <>
      <div className="ns-cmdk-back" data-show={palette.open} onClick={palette.hide} aria-hidden />
      <div
        className="ns-cmdk"
        data-show={palette.open}
        role="dialog"
        aria-modal="true"
        aria-label="Go to page"
        inert={palette.open ? undefined : true}
      >
        {/* New session on every open → fresh query; kept mounted through the fade-out. */}
        {palette.session > 0 ? (
          <PaletteBody key={palette.session} pages={pages} open={palette.open} onClose={palette.hide} />
        ) : null}
      </div>
    </>
  )
}
